import React, { useState } from 'react'
import { useDiagnose } from '../hooks/useDiagnose'

export default function DiagnoseList() {
  const {
    diagnoseMessages,
    loading,
    error,
    customerName,
    userPermissions,
    filterType,
    setFilterType,
    filterDevice,
    setFilterDevice,
    timeRange,
    setTimeRange,
    getTypeIcon,
    getTypeBadgeClass,
    formatTimestamp,
    getMessageSummary,
    isExpired,
    deviceRestrictions,
    isRestricted
  } = useDiagnose()

  const [expandedMessage, setExpandedMessage] = useState(null)
  const [searchQuery, setSearchQuery] = useState('')

  const summary = getMessageSummary()

  // Filter messages based on search query
  const filteredMessages = diagnoseMessages.filter(msg => {
    if (!searchQuery) return true
    
    const query = searchQuery.toLowerCase()
    return (
      msg.Type?.toLowerCase().includes(query) ||
      msg.Device?.toLowerCase().includes(query) ||
      msg.slot?.toLowerCase().includes(query) ||
      msg.credit?.toString().includes(query) ||
      msg['slot-price']?.toString().includes(query)
    )
  })

  const toggleMessageExpansion = (messageId) => {
    setExpandedMessage(expandedMessage === messageId ? null : messageId)
  }

  if (!userPermissions.hasCustomerAccess) {
    return (
      <div className="alert alert-warning">
        <i className="fas fa-exclamation-triangle me-2"></i>
        You don't have access to diagnostic messages. Please contact your administrator.
      </div>
    )
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="h3 mb-0">
          <i className="fas fa-stethoscope me-2"></i>
          Diagnostic Messages
        </h1>
        <div className="d-flex align-items-center gap-2">
          <span className="badge bg-light text-dark">
            Customer: {customerName}
          </span>
          <div className="badge bg-success">
            <i className="fas fa-circle me-1" style={{ fontSize: '0.6rem' }}></i>
            Live
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="row mb-4">
        <div className="col-lg-2 col-md-3 col-6 mb-3">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center">
              <div className="h4 mb-1">{summary.total}</div>
              <div className="text-muted small">Total Messages</div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-3 col-6 mb-3">
          <div className="card border-0 shadow-sm border-start border-warning border-3">
            <div className="card-body text-center">
              <div className="h4 mb-1 text-warning">{summary['invalid price']}</div>
              <div className="text-muted small">Invalid Price</div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-3 col-6 mb-3">
          <div className="card border-0 shadow-sm border-start border-danger border-3">
            <div className="card-body text-center">
              <div className="h4 mb-1 text-danger">{summary['payment error']}</div>
              <div className="text-muted small">Payment Errors</div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-3 col-6 mb-3">
          <div className="card border-0 shadow-sm border-start border-danger border-3">
            <div className="card-body text-center">
              <div className="h4 mb-1 text-danger">{summary['slot malfunction']}</div>
              <div className="text-muted small">Slot Issues</div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-3 col-6 mb-3">
          <div className="card border-0 shadow-sm border-start border-info border-3">
            <div className="card-body text-center">
              <div className="h4 mb-1 text-info">{summary['low inventory']}</div>
              <div className="text-muted small">Low Inventory</div>
            </div>
          </div>
        </div>
        <div className="col-lg-2 col-md-3 col-6 mb-3">
          <div className="card border-0 shadow-sm border-start border-secondary border-3">
            <div className="card-body text-center">
              <div className="h4 mb-1 text-secondary">{summary.expired}</div>
              <div className="text-muted small">Expired</div>
            </div>
          </div>
        </div>
      </div>

      {/* Device restriction banner */}
      {isRestricted && (
        <div className="alert alert-info py-2 mb-3">
          <i className="fas fa-filter me-2"></i>
          Results are limited to your {deviceRestrictions.length} assigned device{deviceRestrictions.length !== 1 ? 's' : ''}.
        </div>
      )}

      {/* Filters */}
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Filter by Type</label>
              <select
                className="form-select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All Types</option>
                <option value="invalid price">Invalid Price</option>
                <option value="payment error">Payment Error</option>
                <option value="slot malfunction">Slot Malfunction</option>
                <option value="low inventory">Low Inventory</option>
                <option value="system error">System Error</option>
                <option value="maintenance">Maintenance</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Filter by Device</label>
              <select
                className="form-select"
                value={filterDevice}
                onChange={(e) => setFilterDevice(e.target.value)}
              >
                <option value="all">All Devices</option>
                {isRestricted
                  ? deviceRestrictions.map(deviceId => (
                      <option key={deviceId} value={deviceId}>{deviceId}</option>
                    ))
                  : [...new Set(diagnoseMessages.map(m => m.Device).filter(Boolean))].sort().map(deviceId => (
                      <option key={deviceId} value={deviceId}>{deviceId}</option>
                    ))
                }
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Time Range</label>
              <select
                className="form-select"
                value={timeRange}
                onChange={(e) => setTimeRange(parseInt(e.target.value))}
              >
                <option value={1}>Last 1 Hour</option>
                <option value={6}>Last 6 Hours</option>
                <option value={24}>Last 24 Hours</option>
                <option value={168}>Last 7 Days</option>
                <option value={720}>Last 30 Days</option>
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Search Messages</label>
              <input
                type="text"
                className="form-control"
                placeholder="Search by type, device, slot..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Messages List */}
      <div className="card border-0 shadow-sm">
        <div className="card-header bg-white">
          <div className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Diagnostic Messages</h6>
            <div className="text-muted small">
              Showing {filteredMessages.length} of {diagnoseMessages.length} messages
            </div>
          </div>
        </div>
        <div className="card-body p-0">
          {loading && (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              <div className="mt-2">Loading diagnostic messages...</div>
            </div>
          )}

          {error && (
            <div className="alert alert-danger m-3">
              <i className="fas fa-exclamation-triangle me-2"></i>
              Error loading diagnostic messages: {error}
            </div>
          )}

          {!loading && !error && filteredMessages.length === 0 && (
            <div className="text-center p-5 text-muted">
              <i className="fas fa-search fa-3x mb-3 d-block"></i>
              {searchQuery ? 'No messages match your search criteria' : 'No diagnostic messages found'}
            </div>
          )}

          {!loading && !error && filteredMessages.length > 0 && (
            <div className="list-group list-group-flush">
              {filteredMessages.map((message) => (
                <div key={message.id} className="list-group-item">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <div className="d-flex align-items-center mb-2">
                        <i className={getTypeIcon(message.Type) + ' me-2'}></i>
                        <span className={getTypeBadgeClass(message.Type) + ' me-2'}>
                          {message.Type?.toUpperCase() || 'UNKNOWN'}
                        </span>
                        {message.Device && (
                          <span className="badge bg-light text-dark me-2">
                            Device: {message.Device}
                          </span>
                        )}
                        {message.slot && (
                          <span className="badge bg-secondary me-2">
                            Slot: {message.slot}
                          </span>
                        )}
                        {isExpired(message.expires) && (
                          <span className="badge bg-danger me-2">
                            <i className="fas fa-clock me-1"></i>
                            Expired
                          </span>
                        )}
                        <span className="text-muted small">
                          {formatTimestamp(message.dateTime)}
                        </span>
                      </div>
                      <div className="mb-2">
                        <strong className="text-truncate d-block">
                          {message.Type} - Credit: {message.credit || 0} vs Slot Price: {message['slot-price'] || 0}
                        </strong>
                      </div>
                      
                      {/* Expandable details */}
                      {expandedMessage === message.id && (
                        <div className="mt-3 p-3 bg-light rounded">
                          <div className="row">
                            <div className="col-md-6">
                              <div className="mb-2">
                                <strong>Device:</strong> {message.Device || 'N/A'}
                              </div>
                              <div className="mb-2">
                                <strong>Type:</strong> {message.Type || 'N/A'}
                              </div>
                              <div className="mb-2">
                                <strong>Slot:</strong> {message.slot || 'N/A'}
                              </div>
                              <div className="mb-2">
                                <strong>Date/Time:</strong> {formatTimestamp(message.dateTime)}
                              </div>
                            </div>
                            <div className="col-md-6">
                              <div className="mb-2">
                                <strong>Credit Amount:</strong> {message.credit || 0}
                              </div>
                              <div className="mb-2">
                                <strong>Slot Price:</strong> {message['slot-price'] || 0}
                              </div>
                              {message.expires && (
                                <div className="mb-2">
                                  <strong>Expires:</strong> {formatTimestamp(message.expires)}
                                  {isExpired(message.expires) && (
                                    <span className="text-danger ms-2">
                                      <i className="fas fa-exclamation-triangle"></i> Expired
                                    </span>
                                  )}
                                </div>
                              )}
                              <div className="mb-2">
                                <strong>Status:</strong> 
                                <span className={`ms-2 ${isExpired(message.expires) ? 'text-danger' : 'text-success'}`}>
                                  {isExpired(message.expires) ? 'Expired' : 'Active'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    <button
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => toggleMessageExpansion(message.id)}
                    >
                      <i className={`fas fa-chevron-${expandedMessage === message.id ? 'up' : 'down'}`}></i>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}